import React from 'react';

import {PolicyType, Collaborator} from 'auth/PermissionConstants';
import {SchemaPolicy} from 'auth/PolicyConstants';
import CollaboratorsSection, {UpdateArgs} from 'collaborator-components/CollaboratorsSection';
import useSchemaPolicyListing from 'collaborator-components/useSchemaPoliciesListing';
import {DefaultSchemaCollaboratorsConfigurationSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface DefaultSchemaCollaboratorsSectionProps {
  activeSchemaType: DefaultSchemaCollaboratorsConfigurationSchemaType;
  collaborators: Array<Collaborator>;
  permissions: {writable: boolean};
  onUpdate: (collaborators: Array<Collaborator>) => void;
  onSubmit?: () => Promise<void>;
  getAdminSchemaPolicy: (policies: Array<SchemaPolicy>) => SchemaPolicy;
  saveButtonDisabled: boolean;
}

function _getDefaultSchemaPolicyId(policies: Array<SchemaPolicy>): string {
  if (policies.length > 0) {
    return policies[0].id;
  }
  return '';
}

export default function DefaultSchemaCollaboratorsSection(
  props: DefaultSchemaCollaboratorsSectionProps
): JSX.Element | null {
  const isReadOnly = !props.permissions.writable;

  function _getAdminSchemaPolicyId(policies: Array<SchemaPolicy>): string {
    const adminPolicy = props.getAdminSchemaPolicy(policies);
    return adminPolicy.id;
  }

  function _handleUpdate(updateArgs: UpdateArgs): void {
    if (updateArgs.collaborators) {
      props.onUpdate(updateArgs.collaborators);
    }
  }

  function _handleSubmit(): void {
    if (!props.onSubmit) {
      return;
    }
    props
      .onSubmit()
      .then(() => {
        notifications.success(`Successfully saved default schema collaborator settings.`);
      })
      .catch(notifications.notifyError);
  }

  const useSchemaPolicyListingResults = useSchemaPolicyListing();

  if (useSchemaPolicyListingResults.queryResultType === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (useSchemaPolicyListingResults.queryResultType !== QueryResultType.SUCCESS) {
    // QueryResultType.ERROR or QueryResultType.SKIPPED
    notifications.error('Could not load schema policies; try reloading the page.');
    return null;
  } else {
    let schemaPolicies = useSchemaPolicyListingResults.schemaPolicies;
    if (props.activeSchemaType === DefaultSchemaCollaboratorsConfigurationSchemaType.INTERFACE) {
      schemaPolicies = schemaPolicies.filter((policy) => policy.name !== 'Create');
    }
    const creatorPolicy = props.getAdminSchemaPolicy(schemaPolicies);
    const creatorRowPropsGroup = {showSchemaCreatorRow: true, creatorPolicy};
    return (
      <div {...getTestProps({component: 'DefaultSchemaCollaboratorsSection', element: 'root'})}>
        <CollaboratorsSection
          getAdminPolicyId={_getAdminSchemaPolicyId}
          getDefaultPolicyId={_getDefaultSchemaPolicyId}
          collaborators={props.collaborators}
          policies={schemaPolicies}
          labelText="Default Options"
          creatorRowPropsGroup={creatorRowPropsGroup}
          enableAuditors={false}
          isReadOnly={isReadOnly}
          onUpdate={_handleUpdate}
          policyType={PolicyType.SCHEMA}
        />
        {!isReadOnly && props.onSubmit && (
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            className="pull-right"
            onClick={_handleSubmit}
            size={ButtonSize.MEDIUM}
            disabled={props.saveButtonDisabled}
            text="Save"
          />
        )}
      </div>
    );
  }
}
